import type { AckMissing, AckPayloads } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'
import { maker } from '@/i18n/messages/pt-BR/marketplace/maker.ts'

/**
 * `marketplace.publish` em português do Brasil — publicar, compartilhar, o ciclo de vida do
 * agente, e as duas frases que uma pessoa é convidada a aceitar.
 *
 * Duas delas são guardadas PALAVRA POR PALAVRA como registro do que alguém leu antes de abrir
 * alguma coisa para mais gente (AGM-042, PRD-27 D-7). O registro guarda também o idioma em que
 * a pessoa leu, e é por isso que esta tradução é parte da trilha de auditoria e não só da tela:
 * quem audita precisa poder verificar que a versão em português dizia a mesma coisa que a
 * versão em inglês.
 *
 * ── O sujeito vem de fora, e isso muda a estrutura das frases ──
 *
 * Várias frases aqui recebem o público como texto pronto: pode ser "Só você" (singular),
 * "Todas as pessoas que entram" (plural), um nome de grupo, ou uma lista de grupos com
 * tamanhos. Em inglês "X can use it" serve a todos eles; em português o verbo concordaria com
 * cada um de um jeito, e nenhuma escolha estaria certa nas quatro formas.
 *
 * Em vez de escolher uma e errar nas outras, essas frases foram reescritas como rótulo e
 * valor — "Quem pode usar: X." — reaproveitando a frase que o D-37 já fixou para este produto.
 * Nada de informação se perde, e não sobra nenhuma concordância para acertar (PRD-27 D-21).
 * O inglês fica exatamente como estava.
 */

/** "12 delas não podem usar Payroll Rules por conta própria" — e "1 delas não pode". */
const beyondClause = (
  fmt: { locale: Locale; formatCount: (value: number) => string },
  beyond: number,
  base: string
): string =>
  pluralCategory(fmt.locale, beyond) === 'one'
    ? `${fmt.formatCount(beyond)} delas não pode usar ${base} por conta própria`
    : `${fmt.formatCount(beyond)} delas não podem usar ${base} por conta própria`

/**
 * As partes de frase que a divulgação divide com as entradas vizinhas.
 *
 * Funções do módulo, e não leituras de `publish` de dentro do próprio `publish`: o objeto
 * referenciar a si mesmo na própria inicialização tiraria do arquivo o tipo que `Messages`
 * depende. As entradas abaixo são invólucros finos, então o diálogo e a releitura da auditoria
 * chamam uma implementação só.
 */
const UNKNOWN_KNOWLEDGE = 'uma base de conhecimento que não está mais disponível para você'
const UNKNOWN_CAPABILITY = 'algo que não está mais disponível'

type PublishAck = AckPayloads['ack.publish']
type Wording = {
  locale: Locale
  formatCount: (value: number) => string
  formatList: (items: readonly string[]) => string
}

/**
 * O público como sujeito, montado a partir do que fica guardado no registro de consentimento.
 *
 * "Só quem mantém" tem três formas (D-37) e elas não são a mesma frase com uma palavra trocada:
 * a forma reflexiva fala com quem lê, e o português conjuga de acordo.
 */
const ackSubjectOf = (
  fmt: { formatList: (items: readonly string[]) => string },
  audience: PublishAck['audience']
): string => {
  if (audience.kind === 'everyone') return 'Todas as pessoas que entram'
  if (audience.kind === 'groups') return `Pessoas de ${fmt.formatList(audience.groups) || 'nenhum grupo ainda'}`
  if (audience.privateKind === 'self') return 'Só você'
  if (audience.privateKind === 'group_managers') return `Só quem gerencia ${audience.privateName ?? ''}`
  return `Só ${audience.privateName ?? ''}`
}

/**
 * Quem a base não alcança, como objeto de "não alcança …".
 *
 * Montado aqui a partir dos números, nunca recebido pronto: "Jurídico (8 pessoas)" junta um
 * número, um parêntese e um substantivo que muda de forma — e o português põe o zero no
 * singular. As palavras são as do construtor, via `maker`, porque a etapa de Conhecimento diz
 * a mesma coisa.
 */
const gapWhomOf = (fmt: Wording, missing: AckMissing): string =>
  missing.kind === 'everyone'
    ? maker.sections.audience.gapEveryone(fmt, missing.people)
    : missing.kind === 'groups'
      ? fmt.formatList(missing.groups.map((group) => maker.sized(fmt, group)))
      : maker.sections.audience.gapPeople(fmt, missing.people)

// "ela" é a base de conhecimento, que é feminina.
const gapLine = (base: string, whom: string): string =>
  `${base} não alcança ${whom}: essas pessoas recebem respostas sem ela`

export const publish: Messages['marketplace']['publish'] = {
  open: 'Publicar',
  openPrivate: 'Ativar',
  republish: 'Publicar alterações',
  republishPrivate: 'Atualizar',
  sendForReview: 'Enviar para revisão',
  restore: 'Trazer de volta',
  title: 'Publicar este agente',
  titlePrivate: 'Ativar este agente',
  titleReview: 'Enviar este agente para revisão',
  titleRestore: 'Trazer este agente de volta',
  lead: 'Este é o momento em que ele passa a ser de outras pessoas. Leia o que você está compartilhando.',
  leadPrivate: 'Ele passa a funcionar — para quem mantém, e para mais ninguém. Leia o que ele vai poder fazer.',
  leadReview:
    'Compartilhar com um grupo que você não gerencia, ou com todas as pessoas, aguarda a revisão de um administrador. Leia o que acontece agora, e o que acontece se a revisão aprovar.',
  leadRestore: 'Ele volta para as pessoas que você escolher. Leia o que elas vão receber.',
  audienceHeading: 'Quem pode usar',
  nowHeading: 'Agora',
  ifApprovedHeading: 'Se um administrador aprovar',
  consentHeading: 'O que elas recebem',
  consentHeadingPrivate: 'O que você recebe',
  now: {
    // Rótulo e valor: `who` chega como "Só você", "Todas as pessoas que entram" ou uma lista de
    // grupos, e nenhum verbo em português concorda com os três ao mesmo tempo (D-21).
    live: (who) => `Quem pode usar assim que você confirmar: ${who}.`,
    liveVersion: 'Todas as pessoas que já podem usar recebem esta versão na hora.',
    liveChanged: (who) => `Esta versão entra no ar na hora. Quem pode usar: ${who}.`,
    waitsDraft: 'Nada muda até um administrador aprovar — ele continua como rascunho, e mais ninguém pode usar.',
    waitsArchived: 'Nada muda até um administrador aprovar — ele continua descontinuado.'
  },
  ifApproved: (who) =>
    `Quem também passa a poder usar: ${who}. As próximas versões chegam a essas pessoas sem uma nova revisão.`,
  ifApprovedEveryone: 'Todas as pessoas que entram podem usar. As próximas versões chegam a elas sem uma nova revisão.',
  ackLabel: 'Li isto e estou compartilhando.',
  ackLabelPrivate: 'Li isto e estou ativando.',
  ackLabelReview: 'Li isto e estou enviando para revisão.',
  ackHint:
    'Só quem mantém pode concordar com isto, e a concordância fica guardada junto com aquilo a que ela se aplica.',
  confirm: 'Publicar',
  confirmPrivate: 'Ativar',
  confirmReview: 'Enviar para revisão',
  confirmRestore: 'Trazer de volta',
  cancel: 'Agora não',
  done: 'Concluído',
  publishing: 'Publicando…',
  sending: 'Enviando…',
  publishedTitle: 'Publicado',
  publishedTitlePrivate: 'Está ativo',
  publishedTitleRestore: 'Está de volta',
  publishedBody: (who) => `Quem pode usar agora: ${who}.`,
  publishedBodyPrivate: (personal) =>
    personal
      ? 'Ele já está no seu seletor de agentes. Mais ninguém vê.'
      : 'Ele já está no seletor de agentes de quem gerencia o grupo. Mais ninguém vê.',
  sentTitle: 'Enviado para revisão',
  sentAndPublishedTitle: 'Publicado, e enviado para revisão',
  sentBody: (who) =>
    `Um administrador vai revisar o compartilhamento com ${who}. Quem revisar vê exatamente o que você acabou de ler, e você vai saber o que for decidido.`,
  sentLiveNote: 'Quem já podia usar está com esta versão agora.',
  sentWaitNote: 'Até lá, ele continua como está.',
  failed: 'Não foi possível publicar.',
  nameTakenTitle: 'Esse nome já está em uso',
  nameTakenBody: 'Outro agente já usa este nome. Escolha um diferente e publique de novo.',
  blockedByLint: 'Corrija os apontamentos acima primeiro.',
  blockedByAudience: 'Escolha quem pode usar primeiro.',
  blockedUnsaved: 'Salve suas alterações primeiro — publicar compartilha a versão salva.',
  blockedNotOwner: 'Só quem mantém pode publicar, escolher quem pode usar ou mover.',
  unknownKnowledge: UNKNOWN_KNOWLEDGE,
  unknownCapability: UNKNOWN_CAPABILITY,
  ackSubject: (fmt, audience) => ackSubjectOf(fmt, audience),
  ackGapWhom: (fmt, missing) => gapWhomOf(fmt, missing),
  /**
   * O registro que a pessoa aceita — e, guardado, o registro do que ela leu (AGM-042, D-7).
   *
   * A frase é montada a partir dos mesmos valores que ficam em `ackParams`, e é isso que torna
   * o registro relido: quem audita pega os parâmetros guardados, passa para a irmã em inglês
   * desta entrada e obtém a frase em inglês, sem tradutor e sem depender de alguém ter mantido
   * os dois catálogos alinhados na mão.
   *
   * `subject` diz DE QUEM a frase fala — de quem está lendo, ou de outras pessoas. Substitui um
   * parâmetro `reader: 'they' | 'you'`, que pedia um pronome do inglês (D-20): em português a
   * distinção cai no verbo, não numa palavra que se encaixa, então o parâmetro passa a declarar
   * o fato e cada idioma decide como dizê-lo.
   *
   * O sujeito vem de fora em quatro formas diferentes e o verbo concordaria com cada uma de um
   * jeito, então a frase é rótulo e valor — "Quem pode usar: X." — reaproveitando a frase que o
   * D-37 já fixou (PRD-27 D-21). O inglês continua como estava.
   */
  consentSentence: (fmt, ack) => {
    const subject = ackSubjectOf(fmt, ack.audience)
    const baseNames = [...ack.bases, ...Array.from({ length: ack.unknownBases }, () => UNKNOWN_KNOWLEDGE)]
    const capabilityNames = [
      ...ack.capabilities,
      ...Array.from({ length: ack.unknownCapabilities }, () => UNKNOWN_CAPABILITY)
    ]
    const knowledge =
      baseNames.length === 0
        ? ack.subject === 'reader'
          ? 'As respostas são baseadas nos documentos que você já pode usar'
          : 'As respostas são baseadas nos documentos que essas pessoas já podem usar'
        : `As respostas são baseadas em ${fmt.formatList(baseNames)}`
    const doing = capabilityNames.length === 0 ? '' : `, e o agente pode usar ${fmt.formatList(capabilityNames)}`
    const missing = ack.gaps.map((gap) => ` ${gapLine(gap.base, gapWhomOf(fmt, gap.missing))}.`).join('')
    return `Quem pode usar: ${subject}. ${knowledge}${doing}.${missing}`
  },
  gapSentence: (base, whom) => gapLine(base, whom),
  audienceSubject: (fmt, kind, groups, privateLabel) =>
    kind === 'everyone'
      ? 'Todas as pessoas que entram'
      : kind === 'private'
        ? privateLabel
        : `Pessoas de ${fmt.formatList(groups) || 'nenhum grupo ainda'}`,
  audiencePhrase: (fmt, kind, groups, privateLabel) =>
    kind === 'everyone'
      ? 'Todas as pessoas que entram'
      : kind === 'private'
        ? privateLabel
        : fmt.formatList(groups) || 'Nenhum grupo ainda',
  // "Só", como em "Só quem mantém" (ADR-0018). O par sujeito/meio-de-frase existe porque
  // recapitalizar uma frase pronta pressupõe uma ortografia (D-20).
  privateOwner: (owner) => `Só ${owner}`,
  privateOwnerPhrase: (owner) => `só ${owner}`,
  privateYou: 'Só você',
  privateYouPhrase: 'só você',
  privateManagers: (group) => `Só quem gerencia ${group}`,
  privateManagersPhrase: (group) => `só quem gerencia ${group}`,

  lifecycle: {
    // Todos os quatro descrevem o agente, que é masculino: "Publicado", nunca "Publicada".
    // "Descontinuado" é como se diz de um produto que foi recolhido e pode voltar; "aposentado"
    // seria uma tradução literal de *retired* que ninguém usa para software.
    state: {
      draft: 'Rascunho',
      published: 'Publicado',
      archived: 'Descontinuado',
      suspended: 'Pausado por um administrador'
    },
    stateHint: {
      draft:
        'Só quem mantém e quem foi convidado a editar consegue ver. Ninguém pode conversar com ele ainda — teste com uma prévia e depois ative.',
      published: 'As pessoas com quem ele foi compartilhado conseguem encontrá-lo.',
      archived: 'As conversas continuam legíveis; ninguém pode começar uma nova.',
      suspended: 'Um administrador parou este agente. O motivo foi informado.'
    },
    publishedHintPrivate: (personal) =>
      personal
        ? 'Só você pode usar. Ele fica fora da galeria de todo mundo.'
        : 'Só quem gerencia o grupo pode usar. Ele fica fora da galeria de todo mundo.',
    unpublish: 'Despublicar',
    unpublishConfirm: 'Voltar para rascunho?',
    unpublishBody:
      'As pessoas perdem ele da galeria. As conversas que elas já têm continuam legíveis, e elas não podem enviar novas mensagens.',
    archive: 'Descontinuar',
    archiveConfirm: 'Descontinuar este agente?',
    archiveBody:
      'Ele some da galeria, e as conversas existentes continuam legíveis. Você pode trazê-lo de volta depois.',
    archiveNameHold: 'O nome dele fica reservado por um tempo depois disso, para ninguém se passar por ele.',
    closesRequest: 'A solicitação que aguarda revisão é encerrada, e quem a enviou recebe um aviso.',
    restore: 'Trazer de volta',
    confirm: 'Sim, pode fazer',
    cancel: 'Cancelar',
    failed: 'Não funcionou.',

    request: {
      // `when` vem de `Intl.RelativeTimeFormat`, que já traz a preposição em português
      // ("há 2 d"). Uma preposição a mais aqui renderizaria "Enviado em há 2 d".
      waiting: (whom, when) => `Aguardando revisão: compartilhamento com ${whom}. Enviado ${when}.`,
      waitingBy: (name) => `Enviado por ${name}.`,
      withdraw: 'Retirar solicitação',
      withdrawing: 'Retirando…',
      withdrawFailed: 'Não foi possível retirar a solicitação.',
      // `whom` pode ser um grupo, uma lista de grupos ou "todas as pessoas que entram": a frase
      // fala do compartilhamento para não depender do número do público (D-21).
      approved: (whom) => `Aprovado: o compartilhamento com ${whom} já está valendo.`,
      approvedBy: (name, when) => (name === null ? `Um administrador aprovou ${when}.` : `${name} aprovou ${when}.`),
      declined: (whom) => `Não aprovado: compartilhamento com ${whom}.`,
      declinedBy: (name, when) => (name === null ? `Um administrador recusou ${when}.` : `${name} recusou ${when}.`),
      closed: (whom) => `A solicitação de compartilhamento com ${whom} foi encerrada.`,
      closedReason: {
        group_deleted: 'Um grupo citado nela foi excluído.',
        requester_lost_authority: 'Quem a enviou não mantém mais este agente.',
        item_unavailable: 'O agente foi despublicado, descontinuado ou pausado.',
        migrated: 'Ela citava grupos herdados do sistema de entrada, que não existem mais aqui.'
      },
      note: (note) => `“${note}”`,
      everyone: 'todas as pessoas que entram',
      dismiss: 'Dispensar'
    }
  },

  lend: {
    action: (base) => `Deixar responder a partir de ${base}`,
    title: (base) => `Deixar este agente responder a partir de ${base}?`,
    lead: 'Leia o que isso significa antes de decidir. Só quem mantém a base de conhecimento pode permitir.',
    // `ownerKind`/`ownerName` no lugar de uma frase pronta: "quem gerencia X" é uma oração
    // relativa em português e não cabe onde o inglês encaixa um possessivo (D-20).
    sentence: (fmt, input) => {
      const owner = input.ownerKind === 'group' ? maker.owner.managersOf(input.ownerName) : input.ownerName
      return [
        `${input.agent} vai responder a partir de ${input.base} — com trechos e fontes — para todas as pessoas que podem usar ${input.agent}: ${maker.people(fmt, input.reach)} com a escolha desta página, e ${beyondClause(fmt, input.beyond, input.base)}.`,
        `Essas pessoas continuam sem poder abrir, navegar ou pesquisar em ${input.base}, nem chegar a ela por meio de outro agente.`,
        `Quando ${input.agent} for compartilhado com mais gente, ele também responde a partir de ${input.base} para essas pessoas, sem perguntar de novo.`,
        `${input.agent} é mantido por ${owner}${input.acts ? ', e pode executar ações — sempre pergunta antes' : ''}.`,
        'Interromper depois não desfaz as respostas que já foram dadas.'
      ].join(' ')
    },
    ack: 'Eu mantenho esta base de conhecimento e permito.',
    confirm: 'Permitir',
    allowing: 'Permitindo…',
    cancel: 'Cancelar',
    failed: 'Não foi possível permitir.'
  }
}
